/**
 * Breadcrumb Configuration
 * Maps route patterns to breadcrumb labels and generates breadcrumb trails automatically
 */

/**
 * Route segment to label mapping
 * Maps URL segments to human-readable labels
 */
export const BREADCRUMB_LABELS = {
  // Admin sections
  'admin': 'Admin',
  'club': 'Dashboard',
  'platform': 'Platform Dashboard',
  
  // Main sections
  'users': 'Users',
  'programs': 'Programs',
  'teams': 'Teams',
  'clubs': 'Clubs',
  'locations': 'Locations',
  'documents': 'Documents',
  'members': 'Members',
  'calendar': 'Calendar',
  'calendars': 'Calendars',
  'events': 'Events',
  'seasons': 'Seasons',
  'roles': 'Roles',
  'skills': 'Skills',
  'settings': 'Settings',
  'tryouts': 'Tryouts',
  'sessions': 'Sessions',
  'templates': 'Templates',
  
  // Actions
  'add': 'Add New',
  'new': 'New',
  'edit': 'Edit',
  'create': 'Create',
  'view': 'View',
  'manage': 'Manage',
  'onboard': 'Onboard',
  
  // Special cases
  'our-team': 'Our Team',
  'about': 'Our Story',
  'approach': 'Approach',
};

/**
 * Route-specific breadcrumb configurations
 * For routes that need special handling beyond simple segment mapping
 */
export const ROUTE_CONFIGS = {
  // User management
  '/admin/users': {
    breadcrumbs: [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Users', path: '/admin/users' }
    ]
  },
  '/admin/users/add': {
    breadcrumbs: [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Users', path: '/admin/users' },
      { label: 'Add New User', path: null }
    ]
  },

  // Role management
  '/admin/roles': {
    breadcrumbs: [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Roles', path: '/admin/roles' }
    ]
  },
  '/admin/roles/add': {
    breadcrumbs: [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Roles', path: '/admin/roles' },
      { label: 'Add New Role', path: null }
    ]
  },

  // Club management (platform admin)
  '/admin/clubs': {
    breadcrumbs: [
      { label: 'Platform Dashboard', path: '/admin/platform' },
      { label: 'Clubs', path: '/admin/clubs' }
    ]
  },
  '/admin/clubs/create': {
    breadcrumbs: [
      { label: 'Platform Dashboard', path: '/admin/platform' },
      { label: 'Clubs', path: '/admin/clubs' },
      { label: 'Create Club', path: null }
    ]
  },
  
  // Programs
  '/admin/programs': {
    breadcrumbs: [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Programs', path: '/admin/programs' }
    ]
  },
  '/admin/programs/new': {
    breadcrumbs: [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Programs', path: '/admin/programs' },
      { label: 'New Program', path: null }
    ]
  },
  
  // Documents
  '/admin/documents': {
    breadcrumbs: [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Documents', path: '/admin/documents' }
    ]
  },
  '/admin/club/document-templates': {
    breadcrumbs: [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Documents', path: '/admin/documents' },
      { label: 'Templates', path: '/admin/club/document-templates' }
    ]
  },
  '/admin/documents/requirements': {
    breadcrumbs: [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Documents', path: '/admin/documents' },
      { label: 'Requirements', path: '/admin/documents/requirements' }
    ]
  },
  
  // Calendar
  '/admin/calendar': {
    breadcrumbs: [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Calendar', path: '/admin/calendar' }
    ]
  },

  // Calendars
  '/admin/calendars': {
    breadcrumbs: [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Calendars', path: '/admin/calendars' }
    ]
  },
  '/admin/calendars/add': {
    breadcrumbs: [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Calendars', path: '/admin/calendars' },
      { label: 'Add New Calendar', path: null }
    ]
  },

  // Seasons
  '/admin/seasons': {
    breadcrumbs: [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Seasons', path: '/admin/seasons' }
    ]
  },
  '/admin/seasons/add': {
    breadcrumbs: [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Seasons', path: '/admin/seasons' },
      { label: 'Add New Season', path: null }
    ]
  },

  // Members
  '/admin/members': {
    breadcrumbs: [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Members', path: '/admin/members' }
    ]
  },
  
  // Settings (club admin)
  '/admin/club/settings': {
    breadcrumbs: [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Club Settings', path: null }
    ]
  },
  
  // Tryouts
  '/admin/tryouts': {
    breadcrumbs: [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Tryouts', path: '/admin/tryouts' }
    ]
  },

  // Branches
  '/admin/branches': {
    breadcrumbs: [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Branches', path: '/admin/branches' }
    ]
  },
  '/admin/branches/create': {
    breadcrumbs: [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Branches', path: '/admin/branches' },
      { label: 'Create Branch', path: null }
    ]
  },
  '/admin/tryouts/sessions/new': {
    breadcrumbs: [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Tryouts', path: '/admin/tryouts?tab=sessions' },
      { label: 'New Session', path: null }
    ]
  },
  '/admin/tryouts/tryouts/new': {
    breadcrumbs: [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Tryouts', path: '/admin/tryouts?tab=tryouts' },
      { label: 'New Tryout', path: null }
    ]
  },
  
  // Platform admin
  '/admin/platform': {
    breadcrumbs: [
      { label: 'Platform Dashboard', path: '/admin/platform' }
    ]
  },
  '/admin/platform/templates': {
    breadcrumbs: [
      { label: 'Platform Dashboard', path: '/admin/platform' },
      { label: 'Templates', path: '/admin/platform/templates' }
    ]
  },
};

/**
 * Pattern-based route matching
 * For routes with dynamic segments (IDs)
 * Returns a function that generates breadcrumbs based on the matched route
 */
export const ROUTE_PATTERNS = [
  {
    // User edit: /admin/users/:userId/edit
    pattern: /^\/admin\/users\/[^/]+\/edit$/,
    generator: () => [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Users', path: '/admin/users' },
      { label: 'Edit User', path: null }
    ]
  },
  {
    // Role edit: /admin/roles/:roleId/edit
    pattern: /^\/admin\/roles\/[^/]+\/edit$/,
    generator: () => [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Roles', path: '/admin/roles' },
      { label: 'Edit Role', path: null }
    ]
  },
  {
    // Club edit: /admin/clubs/:club_id/edit
    pattern: /^\/admin\/clubs\/[^/]+\/edit$/,
    generator: () => [
      { label: 'Platform Dashboard', path: '/admin/platform' },
      { label: 'Clubs', path: '/admin/clubs' },
      { label: 'Edit Club', path: null }
    ]
  },
  {
    // Club view: /admin/clubs/:club_id
    pattern: /^\/admin\/clubs\/[^/]+$/,
    generator: () => [
      { label: 'Platform Dashboard', path: '/admin/platform' },
      { label: 'Clubs', path: '/admin/clubs' },
      { label: 'View Club', path: null }
    ]
  },
  {
    // Program edit: /admin/programs/:programId/edit
    pattern: /^\/admin\/programs\/[^/]+\/edit$/,
    generator: () => [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Programs', path: '/admin/programs' },
      { label: 'Edit Program', path: null }
    ]
  },
  {
    // Tryout session edit: /admin/tryouts/sessions/:sessionId
    pattern: /^\/admin\/tryouts\/sessions\/[^/]+$/,
    generator: () => [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Tryouts', path: '/admin/tryouts?tab=sessions' },
      { label: 'Edit Session', path: null }
    ]
  },
  {
    // Tryout edit: /admin/tryouts/tryouts/:tryoutId
    pattern: /^\/admin\/tryouts\/tryouts\/[^/]+$/,
    generator: () => [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Tryouts', path: '/admin/tryouts?tab=tryouts' },
      { label: 'Edit Tryout', path: null }
    ]
  },
  {
    // Season edit: /admin/seasons/:seasonId/edit
    pattern: /^\/admin\/seasons\/[^/]+\/edit$/,
    generator: () => [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Seasons', path: '/admin/seasons' },
      { label: 'Edit Season', path: null }
    ]
  },
  {
    // Calendar edit: /admin/calendars/:calendarId/edit
    pattern: /^\/admin\/calendars\/[^/]+\/edit$/,
    generator: () => [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Calendars', path: '/admin/calendars' },
      { label: 'Edit Calendar', path: null }
    ]
  },
  {
    // Calendar events: /admin/calendars/:calendarId/events
    pattern: /^\/admin\/calendars\/[^/]+\/events$/,
    generator: () => [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Calendars', path: '/admin/calendars' },
      { label: 'Events', path: null }
    ]
  },
  {
    // Event add: /admin/calendars/:calendarId/events/add
    pattern: /^\/admin\/calendars\/([^/]+)\/events\/add$/,
    generator: (match) => [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Calendars', path: '/admin/calendars' },
      { label: 'Events', path: `/admin/calendars/${match[1]}/events` },
      { label: 'Add New Event', path: null }
    ]
  },
  {
    // Event edit: /admin/calendars/:calendarId/events/:eventId/edit
    pattern: /^\/admin\/calendars\/([^/]+)\/events\/[^/]+\/edit$/,
    generator: (match) => [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Calendars', path: '/admin/calendars' },
      { label: 'Events', path: `/admin/calendars/${match[1]}/events` },
      { label: 'Edit Event', path: null }
    ]
  },
  {
    // Document template create: /admin/club/document-templates/create
    pattern: /^\/admin\/club\/document-templates\/create$/,
    generator: () => [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Documents', path: '/admin/documents' },
      { label: 'Templates', path: '/admin/club/document-templates' },
      { label: 'Create Template', path: null }
    ]
  },
  {
    // Document template edit: /admin/club/document-templates/:templateId/edit
    pattern: /^\/admin\/club\/document-templates\/[^\/]+\/edit$/,
    generator: () => [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Documents', path: '/admin/documents' },
      { label: 'Templates', path: '/admin/club/document-templates' },
      { label: 'Edit Template', path: null }
    ]
  },
  {
    // Branch management: /admin/clubs/:club_id/branches
    pattern: /^\/admin\/clubs\/([^/]+)\/branches$/,
    generator: (pathname) => {
      const match = pathname.match(/^\/admin\/clubs\/([^/]+)\/branches$/);
      const club_id = match ? match[1] : '';
      return [
        { label: 'Dashboard', path: '/admin/club' },
        { label: 'Clubs', path: '/admin/clubs' },
        { label: 'Branches', path: `/admin/clubs/${club_id}/branches` }
      ];
    }
  },
  {
    // Branch create: /admin/clubs/:club_id/branches/create
    pattern: /^\/admin\/clubs\/([^/]+)\/branches\/create$/,
    generator: (pathname) => {
      const match = pathname.match(/^\/admin\/clubs\/([^/]+)\/branches\/create$/);
      const club_id = match ? match[1] : '';
      return [
        { label: 'Dashboard', path: '/admin/club' },
        { label: 'Clubs', path: '/admin/clubs' },
        { label: 'Branches', path: `/admin/clubs/${club_id}/branches` },
        { label: 'Create Branch', path: null }
      ];
    }
  },
  {
    // Branch view: /admin/branches/:branch_id
    pattern: /^\/admin\/branches\/[^/]+$/,
    generator: () => [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Branches', path: '/admin/branches' },
      { label: 'View Branch', path: null }
    ]
  },
  {
    // Branch edit: /admin/branches/:branch_id/edit
    pattern: /^\/admin\/branches\/[^/]+\/edit$/,
    generator: () => [
      { label: 'Dashboard', path: '/admin/club' },
      { label: 'Branches', path: '/admin/branches' },
      { label: 'Edit Branch', path: null }
    ]
  },
];

